import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { buildExcelImportData, type IExcelImportTable, readExcelImportTable } from "@/excel";
import { useFilteredIds } from "@/hooks/useFilteredIds";
import { useTranslations } from "@/queries/useTranslations";
import type { IState } from "@/state";
import { editContent, type IDispatch } from "@/state";
import { buildExportData } from "@/translationFiltering";
import { type IContentType, type ILanguage, LANGUAGES } from "@/types";
import { PreviewTable, PreviewWrapper } from "./ExcelPreview";

function parseKey(fullKey: string): [IContentType, string] {
    const dotIndex = fullKey.indexOf(".");
    if (dotIndex === -1) {
        throw new Error(`Invalid key: ${fullKey}`);
    }
    let contentType = fullKey.substring(0, dotIndex);
    const id = fullKey.substring(dotIndex + 1);
    if (contentType === "default-content") {
        contentType = "defaultContent";
    }
    return [contentType as IContentType, id];
}

interface IProps {
    close: VoidFunction;
}

const OptionsRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem 1rem;
`;

const ErrorMessage = styled.p`
    color: #b00020;
`;

function findHeaderColumn(table: IExcelImportTable, header: string): string | undefined {
    const firstRow = table.rows[0]?.values;
    return table.columns.find((column) => firstRow?.[column]?.trim().toLowerCase() === header.toLowerCase());
}

function ImportModal({ close }: IProps) {
    const dispatch = useDispatch<IDispatch>();
    const { version } = useParams({ from: "/$version" });
    const [lang, setlang] = useState<ILanguage>("en");
    const [table, setTable] = useState<IExcelImportTable>();
    const [keyColumn, setKeyColumn] = useState("A");
    const [valueColumn, setValueColumn] = useState("B");
    const [hasHeaderRow, setHasHeaderRow] = useState(true);
    const [error, setError] = useState<string>();
    const { data: translations } = useTranslations(version);
    const editsByContent = useSelector((state: IState) => state.edits[version]?.data);
    const { filtered } = useFilteredIds(version);
    const referenceData =
        translations && filtered ? buildExportData({ translations, editsByContent, filtered, lang }) : {};

    const handleImport = () => {
        if (!table) return;

        try {
            const importedData = buildExcelImportData(table, { keyColumn, valueColumn, hasHeaderRow });

            // Filter to keep only keys where the imported value differs from referenceData
            const changes = Object.entries(importedData).reduce(
                (acc, [fullKey, newValue]) => {
                    if (referenceData[fullKey] !== newValue) {
                        acc[fullKey] = newValue;
                    }
                    return acc;
                },
                {} as Record<string, string>,
            );

            const edits = Object.entries(changes).map(([fullKey, newValue]) => {
                const [contentType, id] = parseKey(fullKey);
                return { contentType, id, newValue };
            });

            // Dispatch an edit for each change
            for (const { contentType, id, newValue } of edits) {
                dispatch(
                    editContent({
                        version,
                        contentType,
                        lang,
                        id,
                        value: newValue,
                    }),
                );
            }
            close();
        } catch (error) {
            console.error("Error importing Excel file:", error);
            setError(error instanceof Error ? error.message : "Unable to import Excel file.");
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(undefined);
        try {
            const nextTable = await readExcelImportTable(file);
            setTable(nextTable);
            setKeyColumn(findHeaderColumn(nextTable, "id") ?? "A");
            setValueColumn(findHeaderColumn(nextTable, lang) ?? "B");
        } catch (error) {
            console.error("Error reading Excel file:", error);
            setTable(undefined);
            setError(error instanceof Error ? error.message : "Unable to read Excel file.");
        }
    };

    const handleLanguageChange = (nextLang: ILanguage) => {
        setlang(nextLang);
        if (table) {
            setValueColumn(findHeaderColumn(table, nextLang) ?? valueColumn);
        }
    };

    const columnLabel = (column: string) => {
        const header = hasHeaderRow ? table?.rows[0]?.values[column]?.trim() : "";
        return header ? `${column} - ${header}` : column;
    };

    const canImport =
        table && keyColumn !== valueColumn && table.columns.includes(keyColumn) && table.columns.includes(valueColumn);

    return (
        <div>
            <h2>Import answers for {version} as xlsx</h2>
            <OptionsRow>
                <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                />
                {table && (
                    <>
                        <label>
                            Select language to import:{" "}
                            <select value={lang} onChange={(e) => handleLanguageChange(e.target.value as ILanguage)}>
                                {LANGUAGES.map((l) => (
                                    <option key={l} value={l}>
                                        {l}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={hasHeaderRow}
                                onChange={(e) => setHasHeaderRow(e.target.checked)}
                            />{" "}
                            Has header row
                        </label>
                        <label>
                            Key column:{" "}
                            <select value={keyColumn} onChange={(e) => setKeyColumn(e.target.value)}>
                                {table.columns.map((column) => (
                                    <option key={column} value={column}>
                                        {columnLabel(column)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Translation column:{" "}
                            <select value={valueColumn} onChange={(e) => setValueColumn(e.target.value)}>
                                {table.columns.map((column) => (
                                    <option key={column} value={column}>
                                        {columnLabel(column)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </>
                )}
            </OptionsRow>
            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
            {table && (
                <>
                    <PreviewWrapper>
                        <PreviewTable>
                            <thead>
                                <tr>
                                    <th>Row</th>
                                    {table.columns.map((column) => (
                                        <th key={column}>{column}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.slice(0, 10).map((row, index) => (
                                    <tr key={row.number} data-header-row={hasHeaderRow && index === 0}>
                                        <th>{row.number}</th>
                                        {table.columns.map((column) => (
                                            <td key={column} title={row.values[column] ?? ""}>
                                                {row.values[column] ?? ""}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </PreviewTable>
                    </PreviewWrapper>
                    <button type="button" disabled={!canImport} onClick={handleImport}>
                        Import
                    </button>
                </>
            )}
        </div>
    );
}

export default ImportModal;
